import { Activity, useSyncExternalStore, type ReactNode } from "react";
import { AdministratorAuthenticationProvider } from "./authentication-context";
import {
  administrationReadPermission,
  type AdministratorAuthenticationBoundary,
} from "./auth-session";
import type { WebSessionController } from "./web-session-client";

export function SessionBridge({
  children,
  controller,
  expectedOrganizationId,
  convexAuthenticated,
}: Readonly<{
  children: ReactNode;
  controller: WebSessionController;
  expectedOrganizationId: string;
  convexAuthenticated: boolean;
}>) {
  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
  let authentication: AdministratorAuthenticationBoundary;
  if (snapshot.state === "authenticated") {
    authentication =
      snapshot.organizationId !== expectedOrganizationId ||
      !snapshot.permissions.includes(administrationReadPermission)
        ? { state: "denied", reason: "This account does not have aggregate administration access." }
        : {
            state: "authorized",
            user: snapshot.user,
            permissions: [...new Set(snapshot.permissions)].sort(),
          };
  } else
    authentication =
      snapshot.state === "anonymous" ? { state: "anonymous" } : { state: "initializing" };
  const value = {
    authentication,
    signIn: () => {
      globalThis.location.assign("/auth/sign-in");
      return Promise.resolve();
    },
    signOut: async () => {
      const response = await controller.signOut();
      globalThis.location.replace(response.logoutUrl);
    },
  };
  return (
    <AdministratorAuthenticationProvider
      key={snapshot.state === "authenticated" ? snapshot.user.id : snapshot.state}
      value={value}
    >
      {authentication.state === "authorized" && !convexAuthenticated ? (
        <p role="status">
          Reconnecting Site Administration… Your unsaved work is kept in this tab.
        </p>
      ) : null}
      <Activity
        mode={authentication.state === "authorized" && !convexAuthenticated ? "hidden" : "visible"}
      >
        {children}
      </Activity>
    </AdministratorAuthenticationProvider>
  );
}
